// Shared filter logic for products-new list — used by both Search.vue (live option counts)
// and GetData.vue (actual table filtering), so the two never drift apart.

export const FILTER_VERSION = 1

export const OPTIONS = {
  judgmentStatus: [['審査中', '審査中'], ['出品中', '出品中'], ['停止中', '停止中'], ['審査NG', '審査NG'], ['出品NG', '出品NG']],
  stockStatus: [['in', '在庫あり'], ['out', '在庫なし']],
  warehouse: [['Amazon', 'Amazon倉庫'], ['salmonSc', 'STOCKCREW倉庫']],
  adStatus: [['running', '運用中'], ['none', '運用なし']],
  videoStatus: [['has', 'あり'], ['none', 'なし']],
  pickupStatus: [['show', '掲載あり'], ['hide', '掲載なし']]
}

export const TABS = [
  ['judgmentStatus', 'ステータス'],
  ['stockStatus', '在庫数'],
  ['warehouse', '表示する倉庫'],
  ['amount', '受取金額'],
  ['adStatus', '広告運用'],
  ['videoStatus', '動画'],
  ['pickupStatus', 'ピックアップ']
]

// 表示する倉庫 is a display setting, not a real filter — no count shown for it
export const NO_COUNT_KEYS = ['warehouse']

export function getDefaultFilters () {
  return {
    judgmentStatus: [],
    stockStatus: [],
    warehouse: [],
    amountReceived: false,
    adStatus: [],
    pickupStatus: [],
    videoStatus: []
  }
}

// Empty selection within one filter group means "no restriction" == every option selected.
// Exported because 表示する倉庫 is also read directly in ProductsNewGroupedByAsin.vue to decide
// which warehouse info box to render per row — that display check must use the same semantics.
export function effectiveSelection (key, selected) {
  return selected && selected.length ? selected : OPTIONS[key].map(([id]) => id)
}

// 2-option toggle filters (adStatus/videoStatus/pickupStatus): unselected or both selected = show all
export function passesToggleFilter (selected, condition, trueId, falseId) {
  const list = selected || []
  if (!list.length || list.length === 2) return true
  return list.includes(condition ? trueId : falseId)
}

function lower (str) {
  return (str || '').toString().toLowerCase()
}

function matchesKeyword (item, keyword) {
  if (!keyword || !keyword.trim()) return true
  const k = lower(keyword)
  const matches = lower(item.asin).includes(k) ||
    lower(item.productName).includes(k) ||
    lower(item.sku).includes(k) ||
    lower(item.fnsku).includes(k) ||
    (item.skus && Object.values(item.skus).map(v => lower(v)).includes(k)) ||
    (item.skus && Object.keys(item.skus).map(v => lower(v)).includes(k))
  return !!matches
}

/*
 * Apply all Search.vue filter groups to a raw product list.
 * isBasicPlan forces pickupStatus to be ignored (Basic plan has no access to that filter —
 * the UI hides the tab, so the data must not be silently restricted by a stale selection either).
 */
export function applyFilters (allData, filters = {}, keyword = '', isBasicPlan = false) {
  const judgmentStatus = effectiveSelection('judgmentStatus', filters.judgmentStatus)
  const stockStatus = effectiveSelection('stockStatus', filters.stockStatus)
  const warehouse = effectiveSelection('warehouse', filters.warehouse)
  const amountReceived = filters.amountReceived
  const adStatus = filters.adStatus || []
  const videoStatus = filters.videoStatus || []
  const pickupStatus = isBasicPlan ? [] : (filters.pickupStatus || [])

  const showAmz = warehouse.includes('Amazon')
  const showSc = warehouse.includes('salmonSc')
  const hasFilterWarehouse = warehouse.length !== OPTIONS.warehouse.length
  const hasFilterStatus = judgmentStatus.length !== OPTIONS.judgmentStatus.length
  const hasFilterStock = stockStatus.length !== OPTIONS.stockStatus.length

  return (allData || []).filter(item => {
    if (hasFilterWarehouse && !showAmz && (!showSc || !item.hasScOffer)) return false
    if (!matchesKeyword(item, keyword)) return false
    if (hasFilterStatus && !judgmentStatus.includes(item.judgmentStatus)) return false

    const hasStock = (item.numberOfStock && showAmz) || (item.scInventoryQuantity && showSc)
    if (hasFilterStock && stockStatus.includes('in') && !hasStock) return false
    if (hasFilterStock && stockStatus.includes('out') && hasStock) return false

    if (amountReceived && !(item.scOfferPrice >= item.offerPrice)) return false
    if (!passesToggleFilter(adStatus, !!item.promotionRegistered, 'running', 'none')) return false
    if (!passesToggleFilter(pickupStatus, !!item.rakutenPickupRegistered, 'show', 'hide')) return false
    if (!passesToggleFilter(videoStatus, !!item.videoId, 'has', 'none')) return false

    return true
  })
}

// Count = how many items would show if ONLY this one option were checked in this group,
// other groups left as-is. Lets each checkbox preview its own impact.
export function countFor (key, id, allData, filters, keyword, isBasicPlan) {
  const overridden = key === 'amount'
    ? { ...filters, amountReceived: true }
    : { ...filters, [key]: [id] }
  return applyFilters(allData, overridden, keyword, isBasicPlan).length
}



// WEBPACK FOOTER //
// ./src/common/filterLogic.js